import { useCallback, useEffect, useState } from 'react';
import { Typography, Box, Tab, Tabs, Button, IconButton, Divider, Alert, Link } from '@mui/material';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';
import { SelectionList } from 'src/modules/groupMessageEmail/components/selectionList';
import { useTranslation } from 'react-i18next';
import { TabPanel, CreateTabAria } from 'src/shared/components/navigations/tabPanel';
import { getSelectionById } from 'src/shared/services/groupMessageService';
import { useFormContext } from 'react-hook-form';
import { Clear, Launch } from '@mui/icons-material';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { FixedSelection } from '../models/fixedSelection';
import { FixedSelectionList } from './fixedSelectionList';

type SelectionModalProps = {
  smsMode: boolean;
  modalOpen: boolean;
  activeSelection: Selection | null;
  activeFixedSelection: FixedSelection | null;
  onSave: (selection: Selection | null, fixedSelection: FixedSelection | null) => void;
  onClose: () => void;
  selectedRows: Set<number>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
  selection: Selection | null;
  setSelection: (selection: Selection | null) => void;
};

export const SelectionModal = (props: SelectionModalProps) => {
  const { smsMode, modalOpen, activeSelection, activeFixedSelection, onSave, onClose, selectedRows, setSelectedRows, selection, setSelection } =
    props;
  const { t } = useTranslation(['groupMessage']);
  const [activeTab, setActiveTab] = useState<number>(0);
  // const [selection, setSelection] = useState<Selection | null>(null);
  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>(null);

  const { setValue, watch } = useFormContext();
  const excludedMembersCount: number = watch('excludedMemberFromSelection')?.length ?? 0;

  const emptyExcludedMemberHandler = useCallback(() => {
    setValue('excludedMemberFromSelection', undefined);
    setSelectedRows(new Set());
    setSelection(null);
  }, [setSelectedRows, setSelection, setValue]);

  useEffect(() => {
    if (activeSelection) {
      setSelection(activeSelection);
      setActiveTab(0);
    }
  }, [activeSelection, setSelection]);

  useEffect(() => {
    if (activeFixedSelection) {
      setFixedSelection(activeFixedSelection);
      setActiveTab(1);
    }
  }, [activeFixedSelection]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    emptyExcludedMemberHandler();
  };

  const onHandleClose = useCallback(() => {
    emptyExcludedMemberHandler();
    onClose();
  }, [emptyExcludedMemberHandler, onClose]);

  const onHandleSave = useCallback(() => {
    onSave(selection, fixedSelection);
  }, [onSave, selection, fixedSelection]);

  const onSelectionChanged = useCallback(
    async (item: Selection | null) => {
      if (item) {
        const result = await getSelectionById(item?.selection_id);
        setSelection(result.data);
        ConsoleHelper.log('selection from api: ', result.data);
        setFixedSelection(null);
      } else {
        setSelection(item);
      }
    },
    [setSelection],
  );

  const onFixedSelectionChanged = useCallback(
    async (item: FixedSelection | null) => {
      if (item) {
        setFixedSelection(item);
        setSelection(null);
      } else {
        setFixedSelection(item);
      }
    },
    [setSelection],
  );

  const componentId = `genericdialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onHandleClose}
      onSave={onHandleSave}
      disableBackdropClosing
      dialogContent={
        <Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: -2, px: 2, mt: -1, mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ pb: 0 }}>
              <Tab label={t('groupMessage:selection.savedSelections')} {...CreateTabAria(false, componentId, 0)} />
              <Tab label={t('groupMessage:selection.fixedGroups')} {...CreateTabAria(false, componentId, 0)} />
            </Tabs>
          </Box>
          {activeTab === 0 && (
            <TabPanel value={activeTab} index={0} padding={false} {...CreateTabAria(true, componentId, 0)}>
              {!selection && (
                <Typography variant='body1' sx={{ fontWeight: 500 }} gutterBottom>
                  {t('groupMessage:selection.savedSelections')}
                </Typography>
              )}
              <SelectionList
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                callback={onSelectionChanged}
                smsMode={smsMode}
                activeSelection={selection}
              />
              {!selection && (
                <Alert
                  severity='info'
                  sx={{
                    mt: 2,
                    border: (thm) => `1px solid ${thm.palette.divider}`,
                    '& .MuiAlert-message': { width: '100% !important' },
                  }}
                >
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    flexWrap='wrap' // bryt rad på små skärmar
                    gap={1}
                  >
                    <span>{t('groupMessage:selection.helpText')}</span>

                    <Link
                      href='https://hjalp.foreningssupport.se/article/309-medlemsregister-utokad-sokning#versikt-AmvnU'
                      target='_blank'
                      rel='noopener'
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Launch fontSize='small' />
                      {t('groupMessage:selection.helpTextLink')}
                    </Link>
                  </Box>
                </Alert>
              )}
            </TabPanel>
          )}
          {activeTab === 1 && (
            <TabPanel value={activeTab} index={1} padding={false} {...CreateTabAria(true, componentId, 1)}>
              {!fixedSelection && (
                <Typography variant='body1' sx={{ fontWeight: 500 }} gutterBottom>
                  {t('groupMessage:selection.fixedGroups')}
                </Typography>
              )}
              <FixedSelectionList callback={onFixedSelectionChanged} smsMode={smsMode} />
            </TabPanel>
          )}
        </Box>
      }
      dialogActions={
        <Box sx={{ display: 'flex', justifyContent: excludedMembersCount === 0 ? 'flex-end' : 'space-between', alignItems: 'center', width: '100%' }}>
          {excludedMembersCount > 0 && activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <IconButton onClick={emptyExcludedMemberHandler} sx={{ pr: 1 }} size='large'>
                <Clear fontSize='small' />
              </IconButton>
              <Typography variant='h6' sx={{ fontWeight: 400 }}>
                {t('groupMessage:email.excludedCount', { count: excludedMembersCount })}
              </Typography>
              <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            </Box>
          )}
          <Box>
            <Button variant='outlined' color='primary' sx={{ mr: 1 }} disableElevation onClick={onHandleClose}>
              {t('common:button.cancel')}
            </Button>
            <Button autoFocus type='button' variant='contained' color='primary' disableElevation onClick={onHandleSave}>
              {t('common:button.save')}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
